import { Y, TOP, X, LEFT, BOTTOM, RIGHT, ROTATED } from '../../constants.js';
import { LinearConnectionRouter } from './LinearConnectionRouter.js';
import '../../math/Graph.js';
import { Point } from '../../math/Shapes.js';

const topLeft = 'topLeft';
const bottomRight = 'bottomRight';
class CascadingRouter extends LinearConnectionRouter {
    constructor(connection) {
        super(connection);
        this.SAME_SIDE_DISTANCE_RATIO = 5;
        this._connectorSides = [{
                name: TOP,
                axis: Y,
                boundsPoint: topLeft,
                secondarySign: 1
            }, {
                name: LEFT,
                axis: X,
                boundsPoint: topLeft,
                secondarySign: 1
            }, {
                name: BOTTOM,
                axis: Y,
                boundsPoint: bottomRight,
                secondarySign: -1
            }, {
                name: RIGHT,
                axis: X,
                boundsPoint: bottomRight,
                secondarySign: -1
            }];
        this.connection = connection;
    }
    routePoints(start, end, sourceConnector, targetConnector) {
        let result;
        if (sourceConnector && targetConnector) {
            result = this._connectorPoints(start, end, sourceConnector, targetConnector);
        }
        else {
            result = this._floatingPoints(start, end, sourceConnector);
        }
        return result;
    }
    route() {
        const sourceConnector = this.connection._resolvedSourceConnector;
        const targetConnector = this.connection._resolvedTargetConnector;
        const start = this.connection.sourcePoint();
        const end = this.connection.targetPoint();
        const points = this.routePoints(start, end, sourceConnector, targetConnector);
        this.connection.points(points);
    }
    _connectorSide(connector, targetPoint) {
        const position = connector.position();
        const shapeBounds = connector.shape.bounds(ROTATED);
        const bounds = {
            topLeft: shapeBounds.topLeft(),
            bottomRight: shapeBounds.bottomRight()
        };
        const sides = this._connectorSides;
        let min = Number.MAX_VALUE;
        let sideDistance;
        let minSide;
        let axis;
        let side;
        for (let idx = 0; idx < sides.length; idx++) {
            side = sides[idx];
            axis = side.axis;
            sideDistance = Math.round(Math.abs(position[axis] - bounds[side.boundsPoint][axis]));
            if (sideDistance < min) {
                min = sideDistance;
                minSide = side;
            }
            else if (sideDistance === min &&
                (position[axis] - targetPoint[axis]) * side.secondarySign > (position[minSide.axis] - targetPoint[minSide.axis]) * minSide.secondarySign) {
                minSide = side;
            }
        }
        return minSide.name;
    }
    _sameSideDistance(connector) {
        const bounds = connector.shape.bounds(ROTATED);
        return Math.min(bounds.width, bounds.height) / this.SAME_SIDE_DISTANCE_RATIO;
    }
    _connectorPoints(start, end, sourceConnector, targetConnector) {
        const sourceConnectorSide = this._connectorSide(sourceConnector, end);
        const targetConnectorSide = this._connectorSide(targetConnector, start);
        const deltaX = end.x - start.x;
        const deltaY = end.y - start.y;
        const sameSideDistance = this._sameSideDistance(sourceConnector);
        let result = [];
        let pointX, pointY;
        if (sourceConnectorSide === TOP || sourceConnectorSide === BOTTOM) {
            if (targetConnectorSide === TOP || targetConnectorSide === BOTTOM) {
                if (sourceConnectorSide === targetConnectorSide) {
                    if (sourceConnectorSide === TOP) {
                        pointY = Math.min(start.y, end.y) - sameSideDistance;
                    }
                    else {
                        pointY = Math.max(start.y, end.y) + sameSideDistance;
                    }
                    result = [new Point(start.x, pointY), new Point(end.x, pointY)];
                }
                else {
                    result = [new Point(start.x, start.y + deltaY / 2), new Point(end.x, start.y + deltaY / 2)];
                }
            }
            else {
                result = [new Point(start.x, end.y)];
            }
        }
        else {
            if (targetConnectorSide === LEFT || targetConnectorSide === RIGHT) {
                if (sourceConnectorSide === targetConnectorSide) {
                    if (sourceConnectorSide === LEFT) {
                        pointX = Math.min(start.x, end.x) - sameSideDistance;
                    }
                    else {
                        pointX = Math.max(start.x, end.x) + sameSideDistance;
                    }
                    result = [new Point(pointX, start.y), new Point(pointX, end.y)];
                }
                else {
                    result = [new Point(start.x + deltaX / 2, start.y), new Point(start.x + deltaX / 2, start.y + deltaY)];
                }
            }
            else {
                result = [new Point(end.x, start.y)];
            }
        }
        return result;
    }
    _floatingPoints(start, end, sourceConnector) {
        const sourceConnectorSide = sourceConnector ? this._connectorSide(sourceConnector, end) : null;
        const cascadeStartHorizontal = this._startHorizontal(start, end, sourceConnectorSide);
        const points = [start, start, end, end];
        const deltaX = end.x - start.x;
        const deltaY = end.y - start.y;
        const length = points.length;
        let idx = 1;
        let shiftX;
        let shiftY;
        // note that this is more generic than needed for only two intermediate points.
        for (; idx < length - 1; ++idx) {
            if (cascadeStartHorizontal) {
                if (idx % 2 !== 0) {
                    shiftX = deltaX / (length / 2);
                    shiftY = 0;
                }
                else {
                    shiftX = 0;
                    shiftY = deltaY / ((length - 1) / 2);
                }
            }
            else {
                if (idx % 2 !== 0) {
                    shiftX = 0;
                    shiftY = deltaY / (length / 2);
                }
                else {
                    shiftX = deltaX / ((length - 1) / 2);
                    shiftY = 0;
                }
            }
            points[idx] = new Point(points[idx - 1].x + shiftX, points[idx - 1].y + shiftY);
        }
        // need to fix the wrong 1.5 factor of the last intermediate point
        idx--;
        if ((cascadeStartHorizontal && (idx % 2 !== 0)) || (!cascadeStartHorizontal && (idx % 2 === 0))) {
            points[length - 2] = new Point(points[length - 1].x, points[length - 2].y);
        }
        else {
            points[length - 2] = new Point(points[length - 2].x, points[length - 1].y);
        }
        return [points[1], points[2]];
    }
    _startHorizontal(start, end, sourceSide) {
        let horizontal;
        if (sourceSide !== null && (sourceSide === RIGHT || sourceSide === LEFT)) {
            horizontal = true;
        }
        else {
            horizontal = Math.abs(start.x - end.x) > Math.abs(start.y - end.y);
        }
        return horizontal;
    }
}

export { CascadingRouter };
